import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Link from '@/components/Typography/Link';
import { PluginButton } from '@/components/Plugins/PluginControls';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store/auth';
import { fetchWithAuth } from '@/lib/api/client';
import { pluginOwner } from '@/lib/plugins/session';

function calendlyBookingUrl(row) {
  if (row?.state !== 'completed' || row.plugin !== 'bundled:calendly-connected' || row.server !== 'calendly'
      || row.name !== 'scheduling_links-create_single_use_scheduling_link' || row.result?.isError === true) return null;
  const url = row.result?.structuredContent?.resource?.booking_url;
  if (typeof url !== 'string') return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname === 'calendly.com' && !parsed.username && !parsed.password
      && !parsed.port && parsed.pathname.startsWith('/d/') ? url : null;
  } catch { return null; }
}

function ActionCard({ action, userId }) {
  const { colors } = useTheme();
  const [row, setRow] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const mounted = useRef(false), pending = useRef(false), owner = useRef(null);
  const request = async approved => {
    if (pending.current || !mounted.current || !owner.current?.current()) return;
    if (approved !== undefined && (!confirmed || row?.state !== 'pending' || new Date(row.expiresAt).getTime() <= Date.now())) {
      setError('Refresh this action before approving it.'); setConfirmed(false); return;
    }
    const guard = owner.current;
    pending.current = true; setBusy(true); setError(''); setConfirmed(false);
    try {
      const data = await fetchWithAuth(`/json/plugins/chat-actions/${encodeURIComponent(action.id)}`, {
        method: approved === undefined ? 'GET' : 'POST', networkRetries: 0, isRequestCurrent: guard.current,
        ...(approved !== undefined && { body: { approved } }),
      });
      if (!mounted.current || !guard.current()) return;
      if (data.error) throw new Error(data.message || 'This action could not be checked.');
      if (data.id !== action.id) throw new Error('This action changed. Refresh to check it.');
      setRow(data); setConfirmed(true);
    } catch (failure) {
      if (mounted.current && guard.current()) setError(failure.message || 'Check the service before trying again.');
    } finally {
      pending.current = false;
      if (mounted.current && guard.current()) setBusy(false);
    }
  };
  useEffect(() => {
    mounted.current = true;
    try { owner.current = pluginOwner(userId); void request(); }
    catch (failure) { setError(failure.message); }
    return () => { mounted.current = false; owner.current = null; };
  }, [action.id, userId]);
  const shown = row || action;
  const expired = new Date(shown.expiresAt).getTime() <= Date.now();
  const canAct = confirmed && !busy && !error && !expired && row?.state === 'pending';
  const bookingUrl = calendlyBookingUrl(row);
  const statuses = { pending: expired ? 'Approval expired' : 'Review this action before it runs', completed: 'Completed',
    rejected: 'Declined', failed: 'The service reported an error. Check it before requesting another action.',
    running: 'Already submitted. Check the service before requesting another action.',
    unknown: 'Outcome unconfirmed. Check the service before requesting another action.' };
  return <View style={{ borderWidth: 1, borderColor: colors.border_color_secondary, borderRadius: 12, padding: 16, gap: 10 }}>
    <Text size="m" weight="medium">{shown.name.replace(/_/g, ' ')}</Text>
    <Text size="s" color="secondary">{shown.server}</Text>
    <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled><Text size="s" selectable>{JSON.stringify(shown.arguments, null, 2)}</Text></ScrollView>
    <Text size="s" accessibilityLiveRegion="polite">{statuses[shown.state] || 'Check this action'}</Text>
    {!!error && <Text size="s" color="danger" accessibilityRole="alert">{error}</Text>}
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
      {shown.state === 'pending' && <>
        <PluginButton label="Approve action" primary disabled={!canAct} onPress={() => request(true)} />
        <PluginButton label="Decline action" disabled={!canAct} onPress={() => request(false)} />
      </>}
      <PluginButton label="Refresh action" disabled={busy} onPress={() => request()} />
    </View>
    {['completed', 'failed'].includes(row?.state) && row.result && <>
      {bookingUrl && <>
        <Link href={bookingUrl} accessibilityRole="link" accessibilityLabel="Open scheduling link">Open scheduling link</Link>
        <PluginButton label={showDetails ? 'Hide result details' : 'Show result details'} onPress={() => setShowDetails(value => !value)} />
      </>}
      {(!bookingUrl || showDetails) && <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled>
        <Text size="s" selectable>{JSON.stringify(row.result, null, 2)}</Text>
      </ScrollView>}
    </>}
  </View>;
}
export default function ChatPluginActions({ actions, userId }) {
  const currentUserId = useAuthStore(state => state.user?.id);
  const generation = useAuthStore(state => state._sessionGeneration);
  if (!userId || String(currentUserId) !== String(userId) || !actions?.length) return null;
  return <View style={{ gap: 12 }}>{actions.map(action => <ActionCard key={`${userId}:${generation}:${action.id}`} action={action} userId={userId} />)}</View>;
}
